// Omnix/client/ui/WhatsAppTemplateManager.js
import { ComponentBase } from "../core/ComponentBase.js";

export class WhatsAppTemplateManager extends ComponentBase {
    static styles = `
        .wa-template-manager { padding: 1.5rem; }
        .wa-template-item { transition: background-color 0.2s ease-in-out; }
        .status-badge { font-size: 0.8em; font-weight: 600; padding: 0.3em 0.6em; border-radius: 1rem; }
        .status-APPROVED { background-color: var(--bs-success-bg-subtle); color: var(--bs-success-text-emphasis); }
        .status-PENDING { background-color: var(--bs-warning-bg-subtle); color: var(--bs-warning-text-emphasis); }
        .status-REJECTED { background-color: var(--bs-danger-bg-subtle); color: var(--bs-danger-text-emphasis); }
        .status-NOT_SUBMITTED { background-color: var(--bs-secondary-bg-subtle); color: var(--bs-secondary-text-emphasis); }
    `;

    constructor(config) {
        super(config);
        this.templates = [];
        this.channelConfigs = [];
        this.selectedChannelId = null;
        this.applyStyles();
    }

    async initialize() {
        this.container = document.createElement('div');
        this.container.className = 'wa-template-manager';
        this.appClient.events.server.on('whatsappTemplateUpdated', (data) => this.handleTemplateUpdate(data));
        await super.initialize();
    }

    async initAfterLogin() {
        await this.loadInitialData();
    }

    async loadInitialData() {
        try {
            const [templatesResult, channelsResult] = await Promise.all([
                this.appClient.rpcClient.call('TemplateRpc.getData', { filter: { channelType: 'message' }, pageSize: 1000 }),
                this.appClient.rpcClient.call('ChannelManagerRpc.getData', { filter: { channelType: 'whatsapp' }, pageSize: 100 })
            ]);
            this.templates = templatesResult.data || [];
            this.channelConfigs = channelsResult.data || [];
            if (this.channelConfigs.length > 0) {
                this.selectedChannelId = this.channelConfigs[0]._id;
            }
            this.render();
        } catch (error) {
            this.appClient.notifier.error("Failed to load WhatsApp data.");
        }
    }

    handleTemplateUpdate(data) {
        const index = this.templates.findIndex(t => t._id === data.templateId);
        if (index !== -1) {
            this.templates[index].providerStatus = data.status;
            this.templates[index].providerData = data.providerData;
            this.render(); // Re-render the list to show the new status
        }
    }

    render() {
        if (!this.container) return;
        
        const channelOptions = this.channelConfigs.map(c => `<option value="${c._id}" ${c._id === this.selectedChannelId ? 'selected' : ''}>${c.name} (${c.settings.phoneNumberId})</option>`).join('');

        this.container.innerHTML = `
            <div class="d-flex justify-content-between align-items-center mb-4">
                <div>
                    <h3>WhatsApp Template Manager</h3>
                    <p class="text-muted">Submit and sync your message templates with Meta for approval.</p>
                </div>
                <button class="btn btn-primary" id="sync-btn"><i class="bi bi-arrow-clockwise"></i> Sync with Meta</button>
            </div>
            <div class="mb-3">
                <label for="wa-channel-select" class="form-label">Select WhatsApp Channel to Manage:</label>
                <select id="wa-channel-select" class="form-select" ${this.channelConfigs.length === 0 ? 'disabled' : ''}>
                    ${this.channelConfigs.length > 0 ? channelOptions : '<option>No WhatsApp channels configured</option>'}
                </select>
            </div>
            <div class="list-group">
                ${this.templates.map(t => this.renderTemplateItem(t)).join('') || `<div class="list-group-item text-muted">No WhatsApp templates created in Omnix yet.</div>`}
            </div>
        `;

        this.container.querySelector('#wa-channel-select').addEventListener('change', (e) => {
            this.selectedChannelId = e.target.value;
        });
        this.container.querySelector('#sync-btn').addEventListener('click', () => this.handleSync());
        this.container.querySelectorAll('[data-action]').forEach(btn => {
            btn.addEventListener('click', (e) => {
                const action = e.currentTarget.dataset.action;
                const templateId = e.currentTarget.dataset.templateId;
                if (action === 'submit') this.handleSubmit(templateId);
            });
        });
        return this.container;
    }

    renderTemplateItem(template) {
        const status = template.providerStatus || 'NOT_SUBMITTED';
        let actionButton = '';
        if (status === 'NOT_SUBMITTED' || status === 'REJECTED') {
            actionButton = `<button class="btn btn-sm btn-success" data-action="submit" data-template-id="${template._id}">Submit for Approval</button>`;
        } else if (status === 'PENDING') {
            actionButton = `<button class="btn btn-sm btn-secondary" disabled>Pending Review</button>`;
        }

        return `
            <div class="list-group-item wa-template-item">
                <div class="flex-grow-1">
                    <strong>${template.name}</strong>
                    <p class="mb-1 small text-muted">${template.body?.body || 'No body content'}</p>
                </div>
                <div class="d-flex align-items-center gap-3">
                    <span class="status-badge status-${status}">${status.replace('_', ' ')}</span>
                    ${actionButton}
                </div>
            </div>
        `;
    }

    async handleSubmit(templateId) {
        if (!this.selectedChannelId) {
            this.appClient.notifier.error("Please select a WhatsApp channel first.");
            return;
        }
        try {
            await this.appClient.rpcClient.call('WhatsAppRpc.submitTemplateForApproval', {
                channelConfigId: this.selectedChannelId,
                templateId: templateId
            });
            this.appClient.notifier.success("Template submitted to Meta for review.");
            await this.loadInitialData(); // Refresh list
        } catch (error) {
            this.appClient.notifier.error(`Submission failed: ${error.message}`);
        }
    }

    async handleSync() {
        if (!this.selectedChannelId) {
            this.appClient.notifier.error("Please select a WhatsApp channel first.");
            return;
        }
        try {
            const result = await this.appClient.rpcClient.call('WhatsAppRpc.importApprovedTemplates', {
                channelConfigId: this.selectedChannelId
            });
            this.appClient.notifier.success(`Sync complete. Imported ${result.imported} and updated ${result.updated} templates.`);
            await this.loadInitialData();
        } catch (error) {
            this.appClient.notifier.error(`Sync failed: ${error.message}`);
        }
    }
}